import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Switch,
    Button,
    Alert,
    Chip,
    Tooltip,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
    Chat as ChatIcon,
    Extension as ExtensionIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import { themeBackgrounds } from '../../theme/theme';
import { toast } from 'react-toastify';
import chatSettingsData from '../../data/chatSettings.json';
import { useSelector } from 'react-redux';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';

// Chat Settings Types
interface ChatFeature {
    id: string;
    feature: string;
    description: string;
}

const ChatSettingsScreen: React.FC = () => {
    const userDetails = useSelector((state: any) => state.auth.userDetails);
    const applicationDetails = useSelector((state: any) => state.applicationDetails);
    const activePlanDetails = userDetails?.activePlanDetails;
    const chatFeatures = activePlanDetails?.features?.chat || {};
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const planDetails = userDetails?.plan_details;
    const isPlanExpired = Boolean(
        planDetails?.plan_end_date &&
        new Date(planDetails.plan_end_date).getTime() <= Date.now()
    );
    const [mediaFileSharing, setMediaFileSharing] = useState(false);
    const [userPresenceIndicator, setUserPresenceIndicator] = useState(false);
    const [typingIndicator, setTypingIndicator] = useState(false);
    const [messageReactions, setMessageReactions] = useState(false);
    const [publicGroups, setPublicGroups] = useState(false);
    const [deleteSingleMessage, setDeleteSingleMessage] = useState(false);
    const [deleteConversation, setDeleteConversation] = useState(false);

    const [readDeliveryReceipts, setReadDeliveryReceipts] = useState(false);
    const [mentions, setMentions] = useState(false);
    const [threads, setThreads] = useState(false);
    const [quoteReply, setQuoteReply] = useState(false);
    const [privateProtectedGroups, setPrivateProtectedGroups] = useState(false);
    const [searchChatMessages, setSearchChatMessages] = useState(false);
    const [blockUser, setBlockUser] = useState(false);
    const [muteUser, setMuteUser] = useState(false);
    const [banUser, setBanUser] = useState(false);
    const [voiceAudioMessages, setVoiceAudioMessages] = useState(false);
    const [pinMessages, setPinMessages] = useState(false);
    const [pollsInGroups, setPollsInGroups] = useState(false);

    useEffect(() => {
        fetchChatSettings();
    }, [userDetails, applicationDetails]);

    const fetchChatSettings = async () => {
        setIsLoading(true);
        if (!userDetails?.organization_id || !applicationDetails?.app_id) {
            return;
        }
        try {
            const payload = {
                data: await encryptResponse({
                    organization_id: userDetails.organization_id,
                    app_id: applicationDetails.app_id,
                    user_id: userDetails.user_id,
                }, REQ_ENC_KEY)
            };
            const response = await httpService.post<any>(`${API_BASE_URL}/user/getApplicationChatSettings`, payload);
            if (response.data.status === 200 && response.data.data) {
                const data = response.data.data;
                const decrypted = await decryptResponse<{
                    chat_settings: any;
                }>(data, getSessionKey() as string);
                const { chat_settings } = decrypted;
                const settings = chat_settings || {};
                setMediaFileSharing(settings.is_media_file_sharing);
                setUserPresenceIndicator(settings.is_user_presence);
                setTypingIndicator(settings.is_user_typing_indicator);
                setMessageReactions(settings.is_message_reaction);
                setPublicGroups(settings.is_public_group);
                setDeleteSingleMessage(settings.is_delete_chat);
                setDeleteConversation(settings.is_delete_conversation);
                setReadDeliveryReceipts(settings.is_read_and_delivered_receipts);
                setMentions(settings.is_mentions);
                setThreads(settings.is_thread);
                setQuoteReply(settings.is_quote_reply);
                setPrivateProtectedGroups(settings.is_private_and_protected_group);
                setSearchChatMessages(settings.is_search_message);
                setBlockUser(settings.is_block_user);
                setMuteUser(settings.is_mute_user);
                setBanUser(settings.is_ban_user);
                setVoiceAudioMessages(settings.is_voice_message);
                setPinMessages(settings.is_pin_message);
                setPollsInGroups(settings.is_polls);
                console.log('Chat settings from DB', settings);
            } else {
                // setSentimentAnalyticsData(null);
                // setError('Failed to fetch sentiment analytics data');
            }
        } catch (error) {
            // setSentimentAnalyticsData(null);
            // setError('Network error while fetching sentiment analytics data');
        } finally {
            setIsLoading(false);
        }

    };

    // Function to get state value by feature ID
    const getFeatureState = (id: string): boolean => {
        const stateMap: { [key: string]: boolean } = {
            'media-file-sharing': mediaFileSharing,
            'user-presence-indicator': userPresenceIndicator,
            'typing-indicator': typingIndicator,
            'message-reactions': messageReactions,
            'public-groups': publicGroups,
            'delete-single-message': deleteSingleMessage,
            'delete-conversation': deleteConversation,
            'read-delivery-receipts': readDeliveryReceipts,
            'mentions': mentions,
            'threads': threads,
            'quote-reply': quoteReply,
            'private-protected-groups': privateProtectedGroups,
            'search-chat-messages': searchChatMessages,
            'block-user': blockUser,
            'mute-user': muteUser,
            'ban-user': banUser,
            'voice-audio-messages': voiceAudioMessages,
            'pin-messages': pinMessages,
            'polls-in-groups': pollsInGroups,
        };
        return stateMap[id] || false;
    };

    // Function to check if a feature is enabled in the user's plan
    const isFeatureEnabledInPlan = (id: string): boolean => {
        const featurePlanMap: { [key: string]: string } = {
            'media-file-sharing': 'file_sharing',
            'user-presence-indicator': 'user_presence_indicator',
            'typing-indicator': 'typing_indicators',
            'message-reactions': 'reaction_on_messages',
            'public-groups': 'public_groups',
            'delete-single-message': 'delete_single_message',
            'delete-conversation': 'delete_conversation',
            'read-delivery-receipts': 'read_and_delivery_receipts',
            'mentions': 'mentions',
            'threads': 'threaded_messages',
            'quote-reply': 'quote_replies',
            'private-protected-groups': 'private_and_protected_groups',
            'search-chat-messages': 'search_in_messages',
            'block-user': 'block_users',
            'mute-user': 'mute_users',
            'ban-user': 'ban_user',
            'voice-audio-messages': 'voice_audio_messages',
            'pin-messages': 'pin_messages',
            'polls-in-groups': 'polls_in_groups',
        };
        
        const planFeatureKey = featurePlanMap[id];
        if (!planFeatureKey || !chatFeatures) {
            return false;
        }
        
        return chatFeatures[planFeatureKey] === true;
    };

    // Function to set state value by feature ID
    const setFeatureState = (id: string, value: boolean): void => {
        const setterMap: { [key: string]: (value: boolean) => void } = {
            'media-file-sharing': setMediaFileSharing,
            'user-presence-indicator': setUserPresenceIndicator,
            'typing-indicator': setTypingIndicator,
            'message-reactions': setMessageReactions,
            'public-groups': setPublicGroups,
            'delete-single-message': setDeleteSingleMessage,
            'delete-conversation': setDeleteConversation,
            'read-delivery-receipts': setReadDeliveryReceipts,
            'mentions': setMentions,
            'threads': setThreads,
            'quote-reply': setQuoteReply,
            'private-protected-groups': setPrivateProtectedGroups,
            'search-chat-messages': setSearchChatMessages,
            'block-user': setBlockUser,
            'mute-user': setMuteUser,
            'ban-user': setBanUser,
            'voice-audio-messages': setVoiceAudioMessages,
            'pin-messages': setPinMessages,
            'polls-in-groups': setPollsInGroups,
        };
        const setter = setterMap[id];
        if (setter) {
            setter(value);
        }
    };

    // Handle feature toggle
    const handleFeatureToggle = (featureId: string) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setFeatureState(featureId, event.target.checked);
    };

    // Save Settings
    const handleSaveSettings = async () => {
        setIsLoading(true);
        try {
            // Collect all current feature states
            const updatedFlags = {
                    'is_media_file_sharing': mediaFileSharing,
                    'is_user_presence': userPresenceIndicator,
                    'is_message_reaction': messageReactions,
                    'is_public_group': publicGroups,
                    'is_delete_chat': deleteSingleMessage,
                    'is_delete_conversation': deleteConversation,
                    'is_read_and_delivered_receipts': readDeliveryReceipts,
                    'is_mentions': mentions,
                    'is_user_typing_indicator': typingIndicator,
                    'is_thread': threads,
                    'is_quote_reply': quoteReply,
                    'is_private_and_protected_group': privateProtectedGroups,
                    'is_search_message': searchChatMessages,
                    'is_block_user': blockUser,
                    'is_mute_user': muteUser,
                    'is_ban_user': banUser,
                    'is_voice_message': voiceAudioMessages,
                    'is_pin_message': pinMessages,
                    'is_polls': pollsInGroups,
            };

            const payload = {
                data: await encryptResponse({
                    organization_id: userDetails.organization_id,
                    app_id: applicationDetails.app_id,
                    chat_settings: updatedFlags,
                }, REQ_ENC_KEY)
            };
            const response = await httpService.post<any>(`${API_BASE_URL}/user/updateApplicationChatSettings`, payload);
            if (response.data.status !== 200) {
                throw new Error('Failed to update chat settings');
            }
            await fetchChatSettings();
            setShowSuccessAlert(true);
            toast.success('Chat settings updated successfully!', { position: 'top-right' });

            // Hide success alert after 3 seconds
            setTimeout(() => {
                setShowSuccessAlert(false);
            }, 3000);
        } catch (error) {
            toast.error('Failed to update chat settings', { position: 'top-right' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ width: '100%', mx: 'auto' }}>

            {/* Page Header */}
            <PageHeader
                heading="Chat Settings"
                subheading="Configure chat features, extensions, and user experience settings"
                variant="gradient"
            >
                <Chip
                    label={isPlanExpired ? 'Status: Plan Expired' : 'Status: Active'}
                    color={isPlanExpired ? 'error' : 'success'}
                    variant="filled"
                    size="small"
                    sx={{
                        backgroundColor: isPlanExpired
                            ? 'rgba(211, 47, 47, 0.9)'
                            : 'rgba(76, 175, 80, 0.9)',
                        color: '#ffffff',
                        fontWeight: 600,
                    }}
                />
            </PageHeader>

            {/* Success Alert */}
            {showSuccessAlert && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    Chat settings have been updated successfully!
                </Alert>
            )}

            <Grid2 container spacing={1}>
                {/* Core Section */}
                <Grid2 size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <ChatIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6">Core</Typography>
                            </Box>

                            <Grid2 container spacing={2}>
                                {chatSettingsData.core.map((feature: ChatFeature, index: number) => {
                                    const isEnabledInPlan = isFeatureEnabledInPlan(feature.id);
                                    const isEnabled = isEnabledInPlan && !isPlanExpired;
                                    const tooltipTitle = isPlanExpired
                                        ? 'Plan Expire'
                                        : !isEnabledInPlan
                                            ? 'This feature is not available in your current plan. Please upgrade to access this feature.'
                                            : '';
                                    return (
                                        <Grid2 size={{ xs: 12 }} key={feature.id}>
                                            <Tooltip
                                                title={tooltipTitle}
                                                arrow
                                                placement="top"
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: 2,
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 2,
                                                        backgroundColor: '#fafafa',
                                                        gap: 2,
                                                        opacity: isEnabled ? 1 : 0.5,
                                                        cursor: isEnabled ? 'default' : 'not-allowed',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 220,
                                                            minWidth: 220,
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            justifyContent: 'flex-start',
                                                            pr: 1
                                                        }}
                                                    >
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'left' }}>
                                                            {feature.feature}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                                                            {feature.description}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ ml: 'auto' }}>
                                                        <Switch
                                                            checked={getFeatureState(feature.id)}
                                                            onChange={handleFeatureToggle(feature.id)}
                                                            color="primary"
                                                            disabled={!isEnabled}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Tooltip>
                                        </Grid2>
                                    );
                                })}
                            </Grid2>
                        </CardContent>
                    </Card>
                </Grid2>

                {/* Extensions Section */}
                <Grid2 size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <ExtensionIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6">Advanced</Typography>
                            </Box>
                            <Grid2 container spacing={2}>
                                {chatSettingsData.extensions.map((feature: ChatFeature, index: number) => {
                                    const isEnabledInPlan = isFeatureEnabledInPlan(feature.id);
                                    const isEnabled = isEnabledInPlan && !isPlanExpired;
                                    const tooltipTitle = isPlanExpired
                                        ? 'Plan Expire'
                                        : !isEnabledInPlan
                                            ? 'This feature is not available in your current plan. Please upgrade to access this feature.'
                                            : '';
                                    return (
                                        <Grid2 size={{ xs: 12 }} key={feature.id}>
                                            <Tooltip
                                                title={tooltipTitle}
                                                arrow
                                                placement="top"
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',      // keep items vertically centered
                                                        padding: 2,
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 2,
                                                        backgroundColor: '#fafafa',
                                                        gap: 2,
                                                        opacity: isEnabled ? 1 : 0.5,
                                                        cursor: isEnabled ? 'default' : 'not-allowed',
                                                    }}
                                                >
                                                    {/* fixed-width feature title (left aligned) */}
                                                    <Box
                                                        sx={{
                                                            width: 220,              // fixed width for the feature label
                                                            minWidth: 220,          // keep it stable across breakpoints
                                                            display: 'flex',
                                                            alignItems: 'flex-start', // make text sit at top if multi-line
                                                            justifyContent: 'flex-start',
                                                            pr: 1
                                                        }}
                                                    >
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'left' }}>
                                                            {feature.feature}
                                                        </Typography>
                                                    </Box>

                                                    {/* description grows and is left-aligned */}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                                                            {feature.description}
                                                        </Typography>
                                                    </Box>

                                                    {/* push switch to the far right */}
                                                    <Box sx={{ ml: 'auto' }}>
                                                        <Switch
                                                            checked={getFeatureState(feature.id)}
                                                            onChange={handleFeatureToggle(feature.id)}
                                                            color="primary"
                                                            disabled={!isEnabled}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Tooltip>
                                        </Grid2>
                                    );
                                })}
                            </Grid2>

                            {/* Save Button */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                                <Tooltip
                                    title={isPlanExpired ? 'Plan Expire' : ''}
                                    arrow
                                    placement="top"
                                >
                                    <span>
                                        <Button
                                            variant="contained"
                                            onClick={handleSaveSettings}
                                            disabled={isLoading || isPlanExpired}
                                            startIcon={<SaveIcon />}
                                            size="large"
                                            sx={{
                                                background: themeBackgrounds.default,
                                                color: '#ffffff',
                                                px: 4,
                                                '&:hover': {
                                                    background: themeBackgrounds.default,
                                                    opacity: 0.9,
                                                },
                                            }}
                                        >
                                            {isLoading ? 'Saving...' : 'Save Settings'}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid2>
            </Grid2>
        </Box>
    );
};

export default ChatSettingsScreen;

